export interface CodeFormatterOptions {
  printWidth?: number;
  tabWidth?: number;
  useTabs?: boolean;
  semi?: boolean;
  singleQuote?: boolean;
  trailingComma?: "none" | "es5" | "all";
  bracketSpacing?: boolean;
  bracketSameLine?: boolean;
  arrowParens?: "avoid" | "always";
}

export interface CodeFormatterResponse {
  success: boolean;
  formatted?: string;
  error?: string;
}

// Private state
let worker: Worker | null = null;
let currentRequestId = 0;
const pendingRequests: Map<
  number,
  { resolve: (value: string) => void; reject: (reason: Error) => void }
> = new Map();

function initWorker() {
  if (worker) return;

  try {
    worker = new Worker(
      window.Breakdance.builderDistUrl + "/code-formatter.worker.js"
    );

    worker.onmessage = e => {
      const { requestId, success, formatted, error } = e.data;

      const request = pendingRequests.get(requestId);
      if (request) {
        const { resolve, reject } = request;
        pendingRequests.delete(requestId);

        if (success && formatted) {
          resolve(formatted);
        } else {
          reject(new Error(error || "Formatting failed"));
        }
      }
    };
  } catch (error) {
    console.error("Failed to initialize code formatter worker:", error);
  }
}

export async function formatCSS(
  css: string,
  options: CodeFormatterOptions = {}
): Promise<string> {
  if (!worker) {
    initWorker();
  }

  if (!worker) {
    throw new Error("Code formatter worker not initialized");
  }

  // Cancel all previous requests
  for (const [, { reject }] of pendingRequests) {
    reject(new Error("Request superseded by newer request"));
  }
  pendingRequests.clear();

  // Generate new request ID
  const requestId = ++currentRequestId;

  return new Promise((resolve, reject) => {
    pendingRequests.set(requestId, { resolve, reject });

    worker!.postMessage({
      requestId,
      codeToFormat: css,
      parser: "css",
      options
    });
  });
}

export async function formatHTML(
  html: string,
  options: CodeFormatterOptions = {}
): Promise<string> {
  if (!worker) {
    initWorker();
  }

  if (!worker) {
    throw new Error("Code formatter worker not initialized");
  }

  // Cancel all previous requests
  for (const [, { reject }] of pendingRequests) {
    reject(new Error("Request superseded by newer request"));
  }
  pendingRequests.clear();

  // Generate new request ID
  const requestId = ++currentRequestId;

  return new Promise((resolve, reject) => {
    pendingRequests.set(requestId, { resolve, reject });

    worker!.postMessage({
      requestId,
      codeToFormat: html,
      parser: "html",
      options
    });
  });
}

export function destroyCodeFormatter() {
  if (worker) {
    worker.terminate();
    worker = null;
  }

  // Reject all pending requests
  for (const [, { reject }] of pendingRequests) {
    reject(new Error("Code formatter destroyed"));
  }
  pendingRequests.clear();
  currentRequestId = 0;
}
